import { useMutation } from "@apollo/client";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { gql } from "generated-graphql";
import { FacilityPilotProgramReadyStatus, LocalJurisdictionType } from "generated-graphql/graphql";
import { useCallback, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";

const UPSERT_FACILITY_PILOT_PROGRAM_LOCAL_MUTATION = gql(`
  mutation UpsertFacilityPilotProgramLocal($input: UpsertFacilityPilotProgramLocalInput!) {
    upsertFacilityPilotProgramLocal(input: $input) {
      id
      state
      localJurisdictionName
      reportingYear
      status
      notes
      documentationComplete
      qaComplete
      jurisdictionType
      jurisdictionMatchKey
      createdAt
      createdBy
    }
  }
`);

interface EditPilotProgramLocalModalProps {
  open: boolean;
  onClose: () => void;
  state: string;
  localJurisdictionName: string;
  reportingYear: number;
  initialData?: {
    status: FacilityPilotProgramReadyStatus;
    notes: string | null;
    documentationComplete: boolean;
    qaComplete: boolean;
    jurisdictionType?: LocalJurisdictionType | null;
    jurisdictionMatchKey?: string | null;
  };
  onSave: () => void;
}

type FormData = {
  documentationComplete: boolean;
  qaComplete: boolean;
  status: FacilityPilotProgramReadyStatus;
  notes: string;
  jurisdictionType: LocalJurisdictionType | "";
  jurisdictionMatchKey: string;
};

export function EditPilotProgramLocalModal({
  open,
  onClose,
  state,
  localJurisdictionName,
  reportingYear,
  initialData,
  onSave,
}: EditPilotProgramLocalModalProps) {
  const alerts = useAlerts();
  const { control, handleSubmit, reset } = useForm<FormData>({
    defaultValues: {
      documentationComplete: initialData?.documentationComplete ?? false,
      qaComplete: initialData?.qaComplete ?? false,
      status: initialData?.status ?? FacilityPilotProgramReadyStatus.NotReady,
      notes: initialData?.notes ?? "",
      jurisdictionType: initialData?.jurisdictionType ?? "",
      jurisdictionMatchKey: initialData?.jurisdictionMatchKey ?? "",
    },
  });

  // Reset form when initialData changes
  useEffect(() => {
    if (initialData && open) {
      reset({
        documentationComplete: initialData.documentationComplete ?? false,
        qaComplete: initialData.qaComplete ?? false,
        status: initialData.status ?? FacilityPilotProgramReadyStatus.NotReady,
        notes: initialData.notes ?? "",
        jurisdictionType: initialData.jurisdictionType ?? "",
        jurisdictionMatchKey: initialData.jurisdictionMatchKey ?? "",
      });
    }
  }, [initialData, reset, open]);

  const [upsertMutation, { loading: saveLoading }] = useMutation(
    UPSERT_FACILITY_PILOT_PROGRAM_LOCAL_MUTATION,
    {
      onCompleted: () => {
        alerts.success("Local jurisdiction pilot program settings saved");
        onSave();
        onClose();
      },
      onError: (error) => {
        alerts.error(`Failed to save: ${error.message}`);
      },
    }
  );

  const onSubmit = useCallback(
    (formData: FormData) => {
      upsertMutation({
        variables: {
          input: {
            state,
            localJurisdictionName,
            reportingYear,
            status: formData.status,
            notes: formData.notes || null,
            documentationComplete: formData.documentationComplete,
            qaComplete: formData.qaComplete,
            jurisdictionType: formData.jurisdictionType || null,
            jurisdictionMatchKey: formData.jurisdictionMatchKey || null,
          },
        },
      });
    },
    [upsertMutation, state, localJurisdictionName, reportingYear]
  );

  const handleClose = useCallback(() => {
    if (!saveLoading) {
      reset();
      onClose();
    }
  }, [saveLoading, reset, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={`Edit Pilot Program - ${localJurisdictionName}, ${state}`}
      maxWidth="sm"
      fullWidth
      primaryActionButtonProps={{
        loading: saveLoading,
        onClick: handleSubmit(onSubmit),
      }}
    >
      <Box sx={{ pt: 1, px: 3, pb: 3 }}>
        <Stack spacing={1}>
          <RadioGroupField
            name="status"
            control={control}
            label="Ready"
            subtitle="This local jurisdiction is ready to be turned on, don't worry about the status of its pilot facilities"
            radioOptions={[
              {
                value: FacilityPilotProgramReadyStatus.Ready,
                label: "Ready",
              },
              {
                value: FacilityPilotProgramReadyStatus.NotReady,
                label: "Not Ready",
              },
            ]}
          />

          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 1 }}>
              Checklist
            </FormLabel>
            <FormGroup sx={{ mb: 3 }}>
              <Controller
                name="qaComplete"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value ?? false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="QA is complete"
                  />
                )}
              />
              <Controller
                name="documentationComplete"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value ?? false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Documentation is complete"
                  />
                )}
              />
            </FormGroup>
          </FormControl>

          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Notes"
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                placeholder="Add notes about this pilot program..."
              />
            )}
          />

          <Box sx={{ pt: 3 }}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ mb: 2 }}>
                Facility Matching
              </FormLabel>
              <Stack spacing={2}>
                <Controller
                  name="jurisdictionType"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Jurisdiction Type"
                      fullWidth
                      variant="outlined"
                      helperText="The search type for facilities in this jurisdiction (Local FPP only)"
                    >
                      <MenuItem value="">
                        <em>Not Set (uses activity-based matching)</em>
                      </MenuItem>
                      <MenuItem value={LocalJurisdictionType.City}>City</MenuItem>
                      <MenuItem value={LocalJurisdictionType.County}>County</MenuItem>
                      <MenuItem value={LocalJurisdictionType.Township}>Township</MenuItem>
                      <MenuItem value={LocalJurisdictionType.FireDepartment}>
                        Fire Department
                      </MenuItem>
                      <MenuItem value={LocalJurisdictionType.Lepc}>LEPC</MenuItem>
                      <MenuItem value={LocalJurisdictionType.Other}>Other</MenuItem>
                    </TextField>
                  )}
                />
                <Controller
                  name="jurisdictionMatchKey"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Match Key"
                      fullWidth
                      variant="outlined"
                      placeholder="e.g., Chicago, Fairfax, etc."
                      helperText="The search value for facilities in this jurisdiction (Local FPP only)"
                    />
                  )}
                />
              </Stack>
            </FormControl>
          </Box>
        </Stack>
      </Box>
    </Dialog>
  );
}
